import { clientAppFetch } from './clientAppFetch';
import { clientIdentityFields, hasClientIdentity } from './clientIdentity';

const VALID_STATES = new Set(['done', 'skip', 'replaced']);
const VALID_SENDERS = new Set(['client', 'coach']);

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const isIso = (value) => typeof value === 'string' && Number.isFinite(Date.parse(value));
const isDay = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
const isCount = (value) => Number.isInteger(value) && value >= 0;

function parsePlan(value) {
  if (!isObject(value)
      || typeof value.id !== 'string'
      || !value.id
      || (value.status !== 'published' && value.status !== 'archived')
      || !Number.isInteger(value.published_version)
      || value.published_version < 1
      || !isIso(value.active_at)
      || (value.effective_at !== null && !isIso(value.effective_at))
      || (value.published_at !== null && !isIso(value.published_at))) {
    return null;
  }
  return {
    id: value.id,
    status: value.status,
    published_version: value.published_version,
    active_at: value.active_at,
    effective_at: value.effective_at,
    published_at: value.published_at,
  };
}

function parseMeal(value) {
  if (!isObject(value)
      || !isDay(value.day)
      || !VALID_STATES.has(value.state)
      || (value.event_at !== null && !isIso(value.event_at))) {
    return null;
  }
  const resolvedPlan = value.resolved_plan === null ? null : parsePlan(value.resolved_plan);
  if (value.resolved_plan !== null && !resolvedPlan) return null;
  return {
    id: typeof value.id === 'string' ? value.id : null,
    day: value.day,
    event_at: value.event_at,
    set_at: isIso(value.set_at) ? value.set_at : null,
    meal_id: typeof value.meal_id === 'string' ? value.meal_id : null,
    slot: typeof value.slot === 'string' ? value.slot : null,
    meal_title: typeof value.meal_title === 'string' ? value.meal_title : null,
    state: value.state,
    replaced_choice: typeof value.replaced_choice === 'string' ? value.replaced_choice : null,
    plan_version: Number.isInteger(value.plan_version) ? value.plan_version : null,
    resolved_plan: resolvedPlan,
  };
}

export function parseClientLongitudinalSnapshot(value) {
  if (!isObject(value)
      || value.contract_version !== 1
      || value.ok !== true
      || typeof value.found !== 'boolean') {
    return null;
  }
  if (value.found === false) return { contractVersion: 1, found: false };
  if (typeof value.client_id !== 'string'
      || !value.client_id
      || !isObject(value.period)
      || !isIso(value.period.from)
      || !isIso(value.period.to)
      || Date.parse(value.period.from) > Date.parse(value.period.to)
      || !Array.isArray(value.plans)
      || !Array.isArray(value.feedbacks)
      || !Array.isArray(value.messages)
      || !isObject(value.meals)
      || !Array.isArray(value.meals.events)
      || !isObject(value.meals.totals)) {
    return null;
  }

  const plans = value.plans.map((plan) => {
    const activeAt = plan?.effective_at || plan?.published_at;
    return parsePlan({ ...plan, active_at: activeAt });
  });
  const meals = value.meals.events.map(parseMeal);
  if (plans.some((plan) => plan === null) || meals.some((meal) => meal === null)) return null;
  const totals = value.meals.totals;
  if (!['recorded', 'done', 'skipped', 'replaced', 'unresolved'].every((key) => isCount(totals[key]))) return null;
  const activeProgram = value.active_program === null ? null : parsePlan(value.active_program);
  if (value.active_program !== null && !activeProgram) return null;
  if (value.feedbacks.some((row) => !isObject(row) || !isDay(row.date || ''))) return null;
  if (value.messages.some((row) => !isObject(row)
      || typeof row.id !== 'string'
      || !VALID_SENDERS.has(row.sender)
      || !isIso(row.sent_at))) return null;
  if (value.water !== null && (!isObject(value.water)
      || typeof value.water.target_glasses !== 'number'
      || value.water.target_glasses <= 0
      || !Array.isArray(value.water.entries))) return null;

  return {
    contractVersion: 1,
    found: true,
    clientId: value.client_id,
    period: { from: value.period.from, to: value.period.to },
    plans,
    activeProgram,
    feedbacks: value.feedbacks,
    meals: { events: meals, totals: { ...totals } },
    water: value.water,
    messages: value.messages.map((row) => ({ id: row.id, sender: row.sender, sent_at: row.sent_at })),
  };
}

export async function fetchClientLongitudinal(client, { since, to } = {}) {
  if (!hasClientIdentity(client)) return { ok: false, error: 'Cliente sans email ni client_id' };
  if (!isIso(since) || (to != null && !isIso(to))) return { ok: false, error: 'Période longitudinale invalide' };
  let body;
  try {
    body = await clientAppFetch('/api/admin/client-longitudinal', {
      method: 'GET',
      query: { ...clientIdentityFields(client), since, ...(to ? { to } : {}) },
    });
  } catch (error) {
    return { ok: false, error: error?.message || 'Erreur réseau' };
  }
  const snapshot = parseClientLongitudinalSnapshot(body);
  if (!snapshot) return { ok: false, error: 'Historique client invalide' };
  return { ok: true, ...snapshot };
}

export function resolveLongitudinalFetchState(result) {
  if (!result?.ok) {
    return {
      status: 'error',
      snapshot: null,
      error: result?.error || 'Le suivi longitudinal est indisponible.',
    };
  }
  if (result.found === false) {
    return { status: 'unavailable', snapshot: null, error: null };
  }
  if (result.found === true) {
    return { status: 'ready', snapshot: result, error: null };
  }
  return { status: 'error', snapshot: null, error: 'Historique client invalide' };
}
